import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `function deepEqual(a: unknown, b: unknown): boolean {
  // TODO
  return a === b;
}
`;

const solution = `function deepEqual(a: unknown, b: unknown, seen = new WeakMap<object, object>()): boolean {
  // Handles every primitive and identical references, but says false for
  // NaN === NaN and true for 0 === -0, so both are corrected below.
  if (a === b) {
    // The only pair where === is true but the values differ meaningfully.
    return a !== 0 || 1 / (a as number) === 1 / (b as number);
  }
  if (Number.isNaN(a) && Number.isNaN(b)) return true;

  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;

  // Dates and RegExps are objects with no own enumerable keys, so a plain key
  // walk reports every Date equal to every other Date.
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  if (a instanceof RegExp || b instanceof RegExp) {
    return a instanceof RegExp && b instanceof RegExp && a.source === b.source && a.flags === b.flags;
  }

  // An array and an object with keys "0","1" are not the same value.
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  // Cycles. Without this, a self-referencing object recurses until the stack
  // dies, and objects that reference each other are common in real data.
  const prior = seen.get(a as object);
  if (prior !== undefined) return prior === b;
  seen.set(a as object, b as object);

  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    // hasOwnProperty, not \`key in b\`: an inherited property is not an own one,
    // and { a: undefined } must not equal {}.
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key], seen)) {
      return false;
    }
  }
  return true;
}`;

const harness = HARNESS_KIT + `
if (typeof deepEqual !== "function") {
  throw new Error("deepEqual is not defined. Keep the function name as given.");
}

function __same(call, a, b) { __expectExact(call, function () { return deepEqual(a, b); }, true); }
function __diff(call, a, b) { __expectExact(call, function () { return deepEqual(a, b); }, false); }

const __tests = [
  {
    name: "primitives",
    about: "The base case everything else recurses down to.",
    run: function () {
      __same("deepEqual(1, 1)", 1, 1);
      __same("deepEqual('a', 'a')", "a", "a");
      __diff("deepEqual(1, '1')", 1, "1");
      __diff("deepEqual(null, undefined)", null, undefined);
    }
  },
  {
    name: "flat objects",
    about: "Same keys and values, different identities.",
    run: function () {
      __same("deepEqual({a:1,b:2}, {a:1,b:2})", { a: 1, b: 2 }, { a: 1, b: 2 });
      __diff("deepEqual({a:1}, {a:2})", { a: 1 }, { a: 2 });
    }
  },
  {
    name: "key order does not matter",
    about: "Objects are unordered; two literals written differently are the same value.",
    run: function () {
      __same("deepEqual({a:1,b:2}, {b:2,a:1})", { a: 1, b: 2 }, { b: 2, a: 1 });
    }
  },
  {
    name: "nested structures",
    about: "The recursion has to go all the way down, through arrays and objects alike.",
    run: function () {
      __same("nested equal", { a: [1, { b: [2, 3] }] }, { a: [1, { b: [2, 3] }] });
      __diff("nested differing deep", { a: [1, { b: [2, 3] }] }, { a: [1, { b: [2, 4] }] });
    }
  },
  {
    name: "extra keys make objects unequal",
    hidden: true,
    about: "Only walking the first object's keys means every object equals a superset of itself. Compare key counts too.",
    run: function () {
      __diff("deepEqual({a:1}, {a:1,b:2})", { a: 1 }, { a: 1, b: 2 });
      __diff("deepEqual({a:1,b:2}, {a:1})", { a: 1, b: 2 }, { a: 1 });
    }
  },
  {
    name: "NaN equals NaN",
    hidden: true,
    about: "NaN !== NaN, so === alone reports two identical objects unequal because they both contain NaN. For a comparison function that is the wrong answer.",
    run: function () {
      __same("deepEqual(NaN, NaN)", NaN, NaN);
      __same("deepEqual({x:NaN}, {x:NaN})", { x: NaN }, { x: NaN });
    }
  },
  {
    name: "+0 and -0 are not equal",
    hidden: true,
    about: "0 === -0 is true, but they behave differently in division and formatting. The counterpart to the NaN case: both are places === gives the wrong answer.",
    run: function () {
      __diff("deepEqual(0, -0)", 0, -0);
    }
  },
  {
    name: "arrays and array-like objects are different",
    hidden: true,
    about: "An array and { 0: 'a', 1: 'b' } have the same keys. Walking keys alone calls them equal, and then a JSON payload that changed shape compares as unchanged.",
    run: function () {
      __diff("deepEqual(['a','b'], {0:'a',1:'b'})", ["a", "b"], { 0: "a", 1: "b" });
    }
  },
  {
    name: "arrays compare by length and position",
    hidden: true,
    about: "Same members in a different order is a different array.",
    run: function () {
      __diff("deepEqual([1,2,3], [1,3,2])", [1, 2, 3], [1, 3, 2]);
      __diff("deepEqual([1,2], [1,2,3])", [1, 2], [1, 2, 3]);
    }
  },
  {
    name: "dates compare by their time value",
    hidden: true,
    about: "A Date has no own enumerable keys, so a key walk finds nothing to compare and reports every Date equal to every other Date.",
    run: function () {
      __same("same instant", new Date("2026-01-01"), new Date("2026-01-01"));
      __diff("different instants", new Date("2026-01-01"), new Date("2026-06-01"));
    }
  },
  {
    name: "a present undefined is not a missing key",
    hidden: true,
    about: "{ a: undefined } and {} differ. Using 'key in b' or b[key] === undefined conflates them, and that difference survives a round trip through most APIs.",
    run: function () {
      __diff("deepEqual({a:undefined}, {})", { a: undefined }, {});
    }
  },
  {
    name: "cycles do not blow the stack",
    hidden: true,
    about: "Self-referencing structures are ordinary in real data (a parent linking to children linking back). Naive recursion runs until the stack dies, which crashes rather than returning a wrong answer.",
    run: function () {
      const a = { name: "root" };
      a.self = a;
      const b = { name: "root" };
      b.self = b;
      __expectExact("two identical self-referencing objects", function () { return deepEqual(a, b); }, true);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "=== is wrong in two places, in opposite directions, and both are numbers. It says false for NaN against NaN, and true for 0 against -0. Correct both before you go anywhere near recursion.",
    code: `function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    // The one pair where === is true but the values differ meaningfully.
    return a !== 0 || 1 / (a as number) === 1 / (b as number);
  }
  if (Number.isNaN(a) && Number.isNaN(b)) return true;

  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;

  // ... from here on both sides are objects
}`,
  },
  {
    label: "Approach",
    body: "Rule out the object types that carry their value somewhere other than their keys, check the two sides are the same kind of thing, then compare key counts and recurse. hasOwnProperty rather than `in`, so a present undefined is not mistaken for a missing key.",
    code: `// A Date has no own enumerable keys, so a plain key walk finds nothing to
// compare and reports every Date equal to every other Date.
if (a instanceof Date || b instanceof Date) {
  return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
}

// An array and { "0": ..., "1": ... } have the same keys but are not the same value.
if (Array.isArray(a) !== Array.isArray(b)) return false;

const aKeys = Object.keys(a as object);
const bKeys = Object.keys(b as object);
if (aKeys.length !== bKeys.length) return false;   // else every object equals a superset of itself

for (const key of aKeys) {
  if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
  if (!deepEqual((a as any)[key], (b as any)[key])) return false;
}
return true;`,
  },
  {
    label: "The trap",
    body: "A structure that references itself recurses until the stack dies, which crashes rather than returning a wrong answer, and self-referencing data is ordinary. The fix is a WeakMap of pairs already being compared, threaded through the recursion: if you meet a again, the answer is whether it is paired with the same b as last time.",
  },
];

export const deepEqual: Drill = {
  id: "deep-equal",
  track: "typescript",
  title: "Deep equal",
  pattern: "structural-recursion",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `You are writing test helpers and need to compare two API responses by value. Implement \`deepEqual\`.

\`\`\`ts
function deepEqual(a: unknown, b: unknown): boolean
\`\`\`

## Requirements

- **Primitives** compare by value, with no type coercion.
- **Objects** are equal when they have the same keys and every value is deeply equal. Key order is irrelevant.
- **Arrays** compare by length and position, and an array is never equal to a plain object.
- **Dates** compare by their instant.
- **It must not crash on a cycle.**

## Example

\`\`\`ts
deepEqual({ id: 1, tags: ["a"] }, { tags: ["a"], id: 1 });   // true
deepEqual({ id: 1 }, { id: 1, extra: undefined });           // false
deepEqual([1, 2], { 0: 1, 1: 2 });                           // false
\`\`\`

## Where this gets interesting

\`===\` is wrong in exactly two places, in opposite directions. \`NaN === NaN\` is \`false\`, so two identical objects both containing \`NaN\` compare unequal. \`0 === -0\` is \`true\`, though they are distinguishable values. A comparison function has to correct both.

Then the ones that make it a real question rather than a recursion exercise. A \`Date\` has no own enumerable keys, so a key walk finds nothing to compare and cheerfully reports every date equal to every other date. \`{ a: undefined }\` and \`{}\` are different objects, and \`key in b\` cannot tell them apart. And a structure that references itself will recurse until the stack dies, which is a crash rather than a wrong answer.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(n) in the total number of nodes, visiting each once. The cycle guard is a WeakMap of already-compared pairs, which is also what keeps it O(n) rather than exponential on shared subtrees.",
};
